

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_CONFIG } from '../../config/apiConfig';
import { trackPaymentCompleted } from '../../utils/analytics';
import { ClientAreaSkeleton } from '../Skeletons';

const API_BASE_URL = API_CONFIG.USER_API.BASE_URL;

const EcotrimProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user, isPayg } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [accessError, setAccessError] = useState(null);

  useEffect(() => {
    const checkEcotrimAccess = async () => {
      // Si no está autenticado, redirigir al login
      if (!isLoading && !isAuthenticated) {
        navigate('/login', { 
          replace: true,
          state: { 
            from: '/ecotrim',
            message: 'Necesitas iniciar sesión para acceder a Ecotrim'
          }
        });
        return;
      }

      // Esperar a que termine la carga de autenticación
      if (isLoading) {
        return;
      }

      // Si está autenticado, verificar acceso a Ecotrim
      if (isAuthenticated && user) {
        try {
          setIsCheckingAccess(true);
          setAccessError(null);

          // Extraer simulationId de la URL si existe
          // Buscar patrones como /ecotrim/simulation/12345 o /ecotrim?simulationId=12345
          let simulationId = null;
          const urlPath = location.pathname;
          const urlSearchParams = new URLSearchParams(location.search);
          
          // Intentar extraer simulation ID de diferentes formas
          const simulationMatch = urlPath.match(/\/([a-f0-9]{24})(?:\/|$)/);
          if (simulationMatch) {
            simulationId = simulationMatch[1];
          } else if (urlSearchParams.get('simulationId')) {
            simulationId = urlSearchParams.get('simulationId');
          } else if (urlSearchParams.get('simulation')) {
            simulationId = urlSearchParams.get('simulation');
          }

          // Verificar si hay session_id de pago exitoso para activar simulación
          const sessionId = urlSearchParams.get('session_id');
          if (sessionId && simulationId) {
            try {
              const token = localStorage.getItem('authToken');
              
              const verifyResponse = await fetch(`${API_BASE_URL}/api/payments/simulation/${simulationId}/verify`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sessionId })
              });
              
              if (verifyResponse.ok) {
                const verifyData = await verifyResponse.json();
                if (verifyData.success) {
                  trackPaymentCompleted(sessionId);
                }
                // Limpiar URL de parámetros de pago
                window.history.replaceState({}, '', location.pathname);
              }
            } catch (verifyError) {
              console.error('❌ [EcotrimProtectedRoute] Error verificando pago:', verifyError);
            }
          }

          // Pago por uso (IT-606): el plan payg usa el simulador gratis; sus
          // límites se aplican por feature (entitlements), no por suscripción
          if (isPayg) {
            setHasAccess(true);
            setIsCheckingAccess(false);
            return;
          }

          // 1. Verificar suscripción a Ecotrim (método existente)
          const { checkProductAccess } = await import('../../services/subscriptionService');
          const subscriptionResult = await checkProductAccess('ecotrim');


          let hasSubscriptionAccess = false;
          if (subscriptionResult.success && subscriptionResult.hasAccess) {
            hasSubscriptionAccess = true;
          }

          // 2. Si no tiene suscripción pero accede a una simulación específica, verificar acceso pagado
          let hasSimulationAccess = false;
          if (!hasSubscriptionAccess && simulationId) {
            
            try {
              const token = localStorage.getItem('authToken');
              
              const response = await fetch(`${API_BASE_URL}/api/payments/simulation/${simulationId}/access`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (response.ok) {
                const accessData = await response.json();
                
                // Verificar si el usuario tiene acceso pagado a esta simulación específica
                if (accessData.success && accessData.data) {
                  const { hasAccess: simAccess, hasPaid, isOwner } = accessData.data;
                  if (simAccess && (hasPaid || isOwner)) {
                    hasSimulationAccess = true;
                  }
                }
              } else {
              }
            } catch (simError) {
              console.error('❌ [EcotrimProtectedRoute] Error verificando simulación:', simError);
            }
          }

          // 3. Determinar acceso final
          const finalHasAccess = hasSubscriptionAccess || hasSimulationAccess;

          if (finalHasAccess) {
            setHasAccess(true);
          } else {
            setHasAccess(false);
            
            if (!hasSubscriptionAccess && simulationId && !hasSimulationAccess) {
              setAccessError('Para acceder a esta simulación en Ecotrim necesitas una suscripción activa o haber comprado acceso específico a esta simulación.');
            } else {
              setAccessError(subscriptionResult.error || 'No tienes acceso a Ecotrim');
            }
          }
        } catch (error) {
          console.error('❌ [EcotrimProtectedRoute] Error verificando acceso:', error);
          setAccessError('Error al verificar el acceso a Ecotrim');
          setHasAccess(false);
        } finally {
          setIsCheckingAccess(false);
        }
      }
    };

    checkEcotrimAccess();
  }, [isAuthenticated, isLoading, user, navigate, location.pathname, location.search]);

  // Skeleton del simulador mientras arranca auth / se valida acceso.
  if ((isLoading && !isAuthenticated) || (!hasAccess && !accessError)) {
    return <ClientAreaSkeleton variant="simulator" />;
  }

  // Mostrar error de acceso
  if (!hasAccess && accessError) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#fef5e7',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ffd54f',
          borderRadius: '12px',
          padding: '40px 30px',
          maxWidth: '500px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            margin: '0 0 15px 0', 
            color: '#d84315',
            fontSize: '24px'
          }}>
            Acceso Restringido a Ecotrim
          </h2>
          <p style={{ 
            color: '#666', 
            marginBottom: '20px',
            lineHeight: '1.5'
          }}>
            Para acceder a <strong>Ecotrim</strong> necesitas una suscripción activa o haber comprado acceso específico a una simulación. Ecotrim es nuestra herramienta avanzada de optimización energética y análisis de sostenibilidad.
          </p>
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            <strong>¿Cómo obtener acceso?</strong>
            <ul style={{ 
              margin: '8px 0 0 20px',
              paddingLeft: '0',
              color: '#666',
              textAlign: 'left'
            }}>
              <li><strong>Suscripción mensual:</strong> Acceso completo a todas las funciones</li>
              <li><strong>Pago por simulación:</strong> Acceso específico a una simulación individual</li>
              <li><strong>Análisis avanzados:</strong> Optimización de sistemas energéticos</li>
              <li><strong>Informes detallados:</strong> Resultados personalizados y descargables</li>
            </ul>
          </div>
          <p style={{ 
            color: '#888', 
            fontSize: '14px',
            marginBottom: '25px'
          }}>
            Serás redirigido automáticamente para obtener acceso...
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button 
              onClick={() => navigate('/mi-area/herramientas')}
              style={{
                backgroundColor: '#0A7E3E',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Ver Mis Herramientas
            </button>
            <button 
              onClick={() => navigate('/')}
              style={{
                backgroundColor: 'transparent',
                color: '#666',
                border: '1px solid #ddd',
                padding: '12px 24px',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si tiene acceso, renderizar el componente hijo
  return children;
};

export default EcotrimProtectedRoute;